import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ArrowUp, Paperclip } from 'lucide-react';
import { type ChangeEvent, type KeyboardEvent, type ReactNode, useRef, useState } from 'react';

import { EfChatEfAttachment } from '@/presentation/pages/developments/chat/ef-chat-ef-attachment.js';
import { CHAT_ATTACHMENT_ACCEPT } from '@/presentation/pages/developments/chat/utils/attachment-utils.js';
import { primaryCtaGradient } from '@/shared/utils/brand-gradient.js';
import { formatFileSize } from '@/shared/utils/format-file-size.js';

export type EfChatPendingAttachment = {
    id: string;
    fileName: string;
    fileSizeBytes?: number;
    onRemove: () => void;
};

export type EfChatComposerProps = {
    onSend: (content: string) => void;
    disabled?: boolean;
    disabledReason?: string | null;
    isStreaming?: boolean;
    placeholder: string;
    sendLabel: string;
    hint?: string;
    toolbarSlot?: ReactNode;
    leftSlot?: ReactNode;
    actionSlot?: ReactNode;
    attachmentSlot?: ReactNode;
    allowEmptySubmit?: boolean;
    onAttachFiles?: (files: File[]) => void;
    pendingAttachments?: EfChatPendingAttachment[];
    attachmentsEnabled?: boolean;
    attachmentAccept?: string;
    attachLabel?: string;
    removeLabel?: string;
};

export function EfChatComposer({
    onSend,
    disabled = false,
    disabledReason = null,
    isStreaming = false,
    placeholder,
    sendLabel,
    hint,
    toolbarSlot,
    leftSlot,
    actionSlot,
    attachmentSlot,
    allowEmptySubmit = false,
    onAttachFiles,
    pendingAttachments = [],
    attachmentsEnabled = false,
    attachmentAccept = CHAT_ATTACHMENT_ACCEPT,
    attachLabel = 'Anexar arquivo complementar',
    removeLabel = 'Remover anexo'
}: EfChatComposerProps) {
    const [text, setText] = useState('');
    const attachInputRef = useRef<HTMLInputElement | null>(null);
    const readOnly = disabled || isStreaming;
    const canSubmit = (allowEmptySubmit || text.trim().length > 0) && !readOnly;

    const submit = (): void => {
        if (!canSubmit) {
            return;
        }
        const content = text.trim();
        setText('');
        onSend(content);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
        }
    };

    const handleAttachPick = (event: ChangeEvent<HTMLInputElement>): void => {
        const files = event.target.files ? Array.from(event.target.files) : [];
        event.target.value = '';
        if (files.length > 0) {
            onAttachFiles?.(files);
        }
    };

    return (
        <Box data-testid="ef-chat-composer">
            {disabled && disabledReason ? (
                <Typography data-testid="ef-chat-composer-reason" sx={{ mb: 1, fontSize: 12, color: 'text.secondary', textAlign: 'center' }}>
                    {disabledReason}
                </Typography>
            ) : null}
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    opacity: disabled ? 0.7 : 1
                }}
            >
                {attachmentSlot ? <Box sx={{ mb: 1 }}>{attachmentSlot}</Box> : null}
                {pendingAttachments.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1 }} data-testid="ef-chat-pending-attachments">
                        {pendingAttachments.map((attachment) => (
                            <EfChatEfAttachment
                                key={attachment.id}
                                fileName={attachment.fileName}
                                metaLabel={attachment.fileSizeBytes !== undefined ? formatFileSize(attachment.fileSizeBytes) : undefined}
                                onDelete={attachment.onRemove}
                                deleteLabel={removeLabel}
                            />
                        ))}
                    </Stack>
                ) : null}
                <input
                    ref={attachInputRef}
                    type="file"
                    multiple
                    accept={attachmentAccept}
                    onChange={handleAttachPick}
                    style={{ display: 'none' }}
                    data-testid="ef-chat-attach-input"
                />
                <Stack direction="row" alignItems="flex-end" spacing={1}>
                    {leftSlot ? <Box sx={{ flexShrink: 0 }}>{leftSlot}</Box> : null}
                    {attachmentsEnabled && onAttachFiles ? (
                        <IconButton size="small" onClick={() => attachInputRef.current?.click()} aria-label={attachLabel} sx={{ flexShrink: 0 }}>
                            <Paperclip size={16} />
                        </IconButton>
                    ) : null}
                    <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={6}
                        placeholder={placeholder}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={readOnly}
                        variant="standard"
                        inputProps={{ 'aria-label': placeholder, 'data-testid': 'ef-chat-composer-input' }}
                        InputProps={{ disableUnderline: true, sx: { color: 'text.primary', fontSize: 14, py: 0.5 } }}
                    />
                    {toolbarSlot}
                    {actionSlot ? <Box sx={{ flexShrink: 0 }}>{actionSlot}</Box> : null}
                    <IconButton
                        size="small"
                        disabled={!canSubmit}
                        onClick={submit}
                        aria-label={sendLabel}
                        data-testid="ef-chat-composer-send"
                        sx={{
                            flexShrink: 0,
                            color: 'white',
                            background: primaryCtaGradient,
                            '&:hover': { opacity: 0.9, background: primaryCtaGradient },
                            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' }
                        }}
                    >
                        <ArrowUp size={16} />
                    </IconButton>
                </Stack>
            </Box>
            {hint ? <Typography sx={{ mt: 0.75, fontSize: 11, color: 'text.disabled', textAlign: 'center' }}>{hint}</Typography> : null}
        </Box>
    );
}
